import TextButton from "@/components/Common/TextButton";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {useSettingsNavigation} from "@/hooks/useSettingsNavigation";
import {useIsAdmin} from "@/hooks/useUserHooks";
import PackageAdmin from "@/pages/plans/PackageAdmin";
import {useSettingsAgencyStore} from "@/store/agency";
import {useSettingsCompanyStore} from "@/store/company";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import type {TSettingsScope} from "@/store/types";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const AdminBillingPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["multi_account", "settings"]);
  const isAdmin = useIsAdmin();
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const navigateSettings = useSettingsNavigation();
  const scopeAgency = useSettingsAgencyStore(state => state.agency);
  const company = useSettingsCompanyStore(state => state.company);

  const isAgencyScope = scope.type === "agency";
  const agency = isAgencyScope ? scopeAgency : company.agency;
  const scopeId = isAgencyScope ? scopeAgency.id : company.id;

  if (!isAdmin) return null;

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:billing")}</span>
      {scopeId !== scope.id ? (
        <SkeletonLoader className="h-64 rounded-lg" />
      ) : !isAgencyScope && agency?.multiple_brands ? (
        <div className="border-neutral-grey-light-dark flex flex-col items-center gap-5 rounded-lg border px-3 py-5">
          <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-xl font-semibold text-transparent">
            {t(
              agency.large_brand
                ? "multi_account:package_admin_subbrand_level_warning_title_large_brand"
                : "multi_account:package_admin_subbrand_level_warning_title",
            )}
          </span>
          <TextButton
            className="text-sm"
            onClick={() => setSettingsModal({scope: {id: agency.id, type: "agency"}, section: "admin_billing"})}
            text={t(
              agency.large_brand
                ? "multi_account:package_admin_subbrand_level_warning_subtitle_large_brand"
                : "multi_account:package_admin_subbrand_level_warning_subtitle",
            )}
          />
        </div>
      ) : (
        <PackageAdmin
          key={scope.id}
          onManageCredits={mode =>
            navigateSettings({scope, section: "admin_billing", subview: {mode, type: "manage_credits"}})
          }
        />
      )}
    </div>
  );
};

export default AdminBillingPane;
